import {
  Component,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { User } from '../../models/user.model';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '../../services/user.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CreateUserDialogComponent } from './create-user-dialog/create-user-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  Form,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { EditUserDialogComponent } from '../../components/dialogs/edit-user-dialog/edit-user-dialog.component';
import { DeleteUserDialogComponent } from '../../components/dialogs/delete-user-dialog/delete-user-dialog.component';
import { EditVendorDialogComponent } from '../vendors/dialogs/edit-vendor/edit-vendor-dialog.component';
import { EmptyGuid } from '../../constants';
import { Role } from '../../models/role.model';
import { RoleService } from '../../services/role.service';
import { DeleteVendorDialogComponent } from '../vendors/dialogs/delete-vendor/delete-vendor-dialog.component';
import { Statuses } from '../../models/status.enum';
import { MatTableResponsiveModule } from '../../mat-table-responsive.module';
import { CampusService } from '../../services/campus.service';
import { Campus } from '../../models/campus.model';
import { College } from '../../models/college.model';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';

export interface UserTable {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userRole: string;
  lastLoginDate: Date;
  status: string;
}

interface UserFilterForm {
  role: FormControl<string | null>;
  status: FormControl<string | null>;
  campus: FormControl<string | null>;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    MatTabsModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatIconModule,
    MatProgressBarModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableResponsiveModule,
    MatSort,
    MatSortModule,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
  loading: boolean = false;
  queryParams = new Map<string, any>();
  displayedColumns: string[] = [
    'firstName',
    'lastName',
    'email',
    'userProfile.campus.name',
    'userRole',
    'status',
    'manage',
  ];

  statuses: string[] = [];

  dataSource = new MatTableDataSource<User>([]);
  totalCount: number = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize: number = 5;
  page: number = 1;

  allCollege: College = {
    id: EmptyGuid,
    name: '',
    emailDomain: '',
    status: ''
  }

  campuses: Campus[] = [];
  allCampus: Campus = {
    id: EmptyGuid,
    name: 'ALL',
    collegeId: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    isMainCampus: false,
    status: '',
    emailDomain: '',
    college: this.allCollege
  };

  userRoles: Role[] = [];
  allRole: Role = {
    name: 'ALL',
    userRoles: [],
    id: EmptyGuid,
  };

  userFilterForm = new FormGroup<UserFilterForm>({
    role: new FormControl(this.allRole.id),
    status: new FormControl(Statuses.ALL.toString()),
    campus: new FormControl(this.allCampus.id)
  });

  constructor(
    private roleService: RoleService,
    private userService: UserService,
    private campusService: CampusService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    Object.keys(Statuses).forEach((status) => {
      this.statuses.push(status);
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.dataSource.paginator = this.paginator;

    this.getRoles();
    this.getCampuses();
    this.getUsers();
  }

  changePage(event: PageEvent): void {
    this.loading = true;
    this.pageSize = event.pageSize;
    this.page = event.pageIndex + 1;

    this.getUsers();
  }

  getRoles() {
    this.roleService.getUserRoles().subscribe({
      next: (response) => {
        this.userRoles = [...[this.allRole], ...response];
      },
    });
  }

  getCampuses() {
    this.campusService.getAll(undefined, 500, undefined).subscribe({
      next: (response) => {
        this.campuses = [...[this.allCampus], ...response.items];
      },
    });
  }

  getUsers() {
    this.loading = true;

    this.userService
      .getAll(this.page, this.pageSize, this.queryParams)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.dataSource.data = response.items;

          this.totalCount = response.totalCount;
        },
        error: () => {
          this.snackBar.open(
            'An error occured while retrieving users. Please try again.',
            '',
            { duration: 3000 }
          );
        },
      });
  }

  openCreateDialog() {
    const dialogRefCreate = this.dialog.open(CreateUserDialogComponent);

    dialogRefCreate.afterClosed().subscribe((result) => {
      if (result) {
        this.snackBar.open(result.message, '', { duration: 4000 });
      }
    });
  }

  openEditDialog(user: User) {
    let dialogRefEdit;

    if (user.vendorId) {
      dialogRefEdit = this.dialog.open(EditVendorDialogComponent, {
        height: '65%',
        width: '70%',
        data: { vendorIdToEdit: user.vendorId },
      });
    } else {
      dialogRefEdit = this.dialog.open(EditUserDialogComponent, {
        height: '65%',
        width: '70%',
        data: { userId: user.id },
      });
    }

    dialogRefEdit.afterClosed().subscribe((result) => {
      this.getUsers();
    });
  }

  openDeleteDialog(user: User) {
    let dialogRefDelete;

    if (user.vendorId) {
      dialogRefDelete = this.dialog.open(DeleteVendorDialogComponent, {
        data: { vendorId: user.vendorId },
      });
    } else {
      dialogRefDelete = this.dialog.open(DeleteUserDialogComponent, {
        data: { userId: user.id },
      });
    }

    dialogRefDelete.afterClosed().subscribe((result) => {
      if (result) {
        this.snackBar.open(result.message, '', { duration: 4000 });
      }

      this.getUsers();
    });
  }

  getUsersWithRole(event: MatSelectChange) {
    this.queryParams.set('roleId', event.value);

    this.getUsers();
  }

  getUsersWithStatus(event: MatSelectChange) {
    this.queryParams.set('status', event.value);

    this.getUsers();
  }

  getUsersWithCampus(event: MatSelectChange) {
    this.queryParams.set('campusId', event.value);

    this.getUsers();
  }

    sortChange(event: Sort) {
      this.queryParams.delete('OrderBy');

      if (event != undefined && event.direction != '') {
        var sort = `${event.active}%20${event.direction}`;
        this.queryParams.set('OrderBy', sort);
      }
  
      this.getUsers();
    }
}
